import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { gsap } from 'gsap';
import { PlayingCard } from '../../poker-ui/PlayingCard';
import { cn } from '../../poker-ui/utils/cn';

/**
 * Единый козырь для UI: `trumpSuit` / `trump`, число 0..3, `all_trump`, без козыря → `null`.
 * @param {Record<string, unknown>|null|undefined} contract
 * @returns {number|'all_trump'|null|undefined} `undefined` — невалидное значение масти
 */
export function normalizeDeckTrumpSuit(contract) {
  if (!contract || typeof contract !== 'object') return undefined;
  const raw = contract.trumpSuit ?? contract.trump;
  if (raw === 'all_trump') return 'all_trump';
  if (raw == null || raw === 'no_trump') return null;
  const n = Number(raw);
  if (Number.isInteger(n) && n >= 0 && n <= 3) return n;
  return undefined;
}

/** Пунктирный слот под колодой, повёрнут как финальная карта-козырь. */
const PLACEHOLDER_SLOT_CLASS =
  'flex h-16 w-12 shrink-0 items-center justify-center rounded-lg border border-dashed border-white/40 bg-black/25 shadow-inner';

/** Как у лицевой `PlayingCard` size="sm" + кольцо козыря у колоды. */
const TRUMP_CARD_FACE_CLASS =
  'relative h-16 w-12 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-slate-100 shadow-[0_6px_18px_rgba(0,0,0,0.45)] ring-1 ring-amber-300/35';

/** Ось: якорь у нижнего правого угла пачки, карта под 90° частично из-под рубашек. */
const TRUMP_ANCHOR_CLASS =
  'pointer-events-none absolute bottom-[1px] right-[1px] z-0 flex h-0 w-0 items-end justify-end overflow-visible';

/**
 * Сдвиг после rotate-90: более отрицательный translateX вытаскивает угол из-под рубашек.
 * Текущие значения −28px / −32px (sm) — на 4px ближе к колоде, чем −32/−36.
 */
const TRUMP_ROTATE_CLASS =
  'origin-center rotate-90 -translate-x-[28px] translate-y-0.5 sm:-translate-x-[32px] sm:translate-y-1';

/**
 * Слот «козырь у колоды»: пустая рамка в торгах/раздаче, после контракта — туз масти (украш.) или плашка БК / AT.
 *
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {string} props.phase
 * @param {Record<string, unknown>|null|undefined} props.contract
 * @param {number} props.roundIndex
 * @param {number} [props.auctionRevealMs]
 * @param {boolean} props.prefersReducedMotion
 * @param {(cardId: number) => { rank: string, suit: string }} props.cardIdToPlaying
 */
export function BelotDeckTrumpIndicator({
  enabled,
  phase,
  contract,
  roundIndex,
  auctionRevealMs = 2350,
  prefersReducedMotion,
  cardIdToPlaying,
}) {
  const animHostRef = useRef(null);
  const lastAnimatedRevealKeyRef = useRef('');

  const showPlaceholder = useMemo(() => {
    if (!enabled) return false;
    if (contract) return false;
    return phase === 'auction' || phase === 'dealing' || phase === 'init';
  }, [enabled, contract, phase]);

  const normalizedTrumpSuit = useMemo(() => normalizeDeckTrumpSuit(contract), [contract]);

  const revealKey = useMemo(() => {
    if (!contract) return `empty-${roundIndex}`;
    const ts = normalizedTrumpSuit;
    const pts = Number(contract.contractTargetPoints ?? contract.points ?? 0);
    return `${roundIndex}:${String(ts)}:${pts}`;
  }, [contract, normalizedTrumpSuit, roundIndex]);

  const trumpVisual = useMemo(() => {
    if (!contract) return null;
    const ts = normalizedTrumpSuit;
    if (ts === 'all_trump') {
      const corner = (
        <div className="leading-none text-slate-900">
          <span className="block text-xs font-bold">АТ</span>
        </div>
      );
      return (
        <div className={cn(TRUMP_CARD_FACE_CLASS)} title="Все козыри">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/10 to-transparent pointer-events-none" />
          <div className="absolute left-0 top-0 z-10">{corner}</div>
          <div className="absolute inset-0 flex items-center justify-center text-slate-900">
            <span className="text-2xl font-bold leading-none tracking-tight">АТ</span>
          </div>
          <div className="absolute bottom-0 right-0 z-10 rotate-180">{corner}</div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent" />
        </div>
      );
    }
    if (ts == null) {
      const corner = (
        <div className="leading-none text-slate-900">
          <span className="block text-xs font-bold">БК</span>
        </div>
      );
      return (
        <div className={cn(TRUMP_CARD_FACE_CLASS)} title="Без козыря">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-500/10 to-transparent pointer-events-none" />
          <div className="absolute left-0 top-0 z-10">{corner}</div>
          <div className="absolute inset-0 flex items-center justify-center text-slate-900">
            <span className="text-2xl font-bold leading-none tracking-tight">БК</span>
          </div>
          <div className="absolute bottom-0 right-0 z-10 rotate-180">{corner}</div>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent" />
        </div>
      );
    }
    const suit = Number(ts);
    if (!Number.isInteger(suit) || suit < 0 || suit > 3) return null;
    const aceId = suit * 8 + 7;
    const { rank, suit: playingSuit } = cardIdToPlaying(aceId);
    return (
      <div title="Козырь (туз масти — для наглядности)">
        <PlayingCard
          rank={rank}
          suit={playingSuit}
          size="sm"
          tightCorners
          className="shadow-[0_6px_18px_rgba(0,0,0,0.45)] ring-1 ring-amber-300/35"
        />
      </div>
    );
  }, [cardIdToPlaying, contract, normalizedTrumpSuit]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const host = animHostRef.current;
    if (!contract || !host) {
      lastAnimatedRevealKeyRef.current = '';
      if (host) gsap.set(host, { clearProps: 'opacity,x,y,scale' });
      return undefined;
    }
    gsap.killTweensOf(host);
    if (prefersReducedMotion) {
      gsap.set(host, { opacity: 1, x: 0, y: 0, scale: 1 });
      lastAnimatedRevealKeyRef.current = revealKey;
      return undefined;
    }
    if (lastAnimatedRevealKeyRef.current === revealKey) {
      gsap.set(host, { opacity: 1, x: 0, y: 0, scale: 1 });
      return undefined;
    }
    const delaySec = Math.min(0.34, Math.max(0, auctionRevealMs) * 0.09 / 1000);
    gsap.fromTo(
      host,
      { opacity: 0, x: 18, y: 12, scale: 0.86 },
      {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: 0.52,
        ease: 'power2.out',
        delay: delaySec,
        onComplete: () => {
          lastAnimatedRevealKeyRef.current = revealKey;
        },
      },
    );
    return () => {
      gsap.killTweensOf(host);
    };
  }, [auctionRevealMs, contract, enabled, prefersReducedMotion, revealKey]);

  if (!enabled) return null;

  return (
    <div className={cn(TRUMP_ANCHOR_CLASS)} aria-hidden>
      {showPlaceholder ? (
        <div className={TRUMP_ROTATE_CLASS} title="Место карты-козыря после bazar">
          <div className={PLACEHOLDER_SLOT_CLASS} />
        </div>
      ) : null}
      {contract && trumpVisual ? (
        <div className={TRUMP_ROTATE_CLASS}>
          <div ref={animHostRef}>{trumpVisual}</div>
        </div>
      ) : null}
    </div>
  );
}
